import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Organization } from '@app/models';
import {
  HelpDeskService,
  N8nChatHandlerService,
  OrganizationService,
  SessionConfigService,
  UserService,
  WebsocketService,
} from '@app/services';
import { AiAssistantService } from '@evollo/ai-assistant';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectConfig } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgxPermissionsService } from 'ngx-permissions';
import { delay } from 'rxjs/operators';
import * as config from '../assets/config.json';
import { environment } from '../environments/environment';
import { HelpDialogComponent } from './_components/help-dialog/help-dialog.component';
import { UserInterface } from './_models/interfaces/user.interface';
import { AccessLogService } from './_services/access-log.service';
import { ThemeService } from './_services/theme.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  public currentUser: UserInterface;
  public currentToken: string;
  public currentOrganization: Organization;
  public changePassword = false;
  public open: boolean = true;
  public modalRef: BsModalRef;

  public permissionsLoaded: boolean = false;
  private config = config;

  public aiEnabled: boolean = environment.features.aiEnabled;
  public genAiUrl: string = environment.genAiUrl;

  public awsOutbreakBannerActive: boolean = false;

  constructor(
    public router: Router,
    private ngSelectConfig: NgSelectConfig,
    private userService: UserService,
    public organizationService: OrganizationService,
    public permissionsService: NgxPermissionsService,
    private modal: NgbModal,
    public helpDeskService: HelpDeskService,
    private httpClient: HttpClient,
    translate: TranslateService,
    private aiAssistantService: AiAssistantService,
    private accessLogService: AccessLogService,
    private elementRef: ElementRef,
    private websocketService: WebsocketService,
    private n8nChatHandlerService: N8nChatHandlerService,
    private themeService: ThemeService
  ) {
    translate.addLangs(['pt-br']);
    translate.setDefaultLang('pt-br');
    translate.use('pt-br');

    this.userService.currentUser$.subscribe(user => {
      this.currentUser = user;
      this.currentOrganization = this.organizationService.currentOrganization;
      this.ngSelectConfig.notFoundText = 'Não encontrado';
      this.currentToken = this.userService.jwtToken;
    });
  }

  public ngOnInit(): void {
    // Initialize theme from localStorage as fallback (before user login)
    // When user logs in, theme will be initialized from user preferences
    this.themeService.initTheme();
    this.listenToPermissions();
  }

  public ngAfterViewInit(): void {
    this.initializeWebsocketWithRetry(0);
  }

  private initializeWebsocketWithRetry(attempt: number): void {
    const maxAttempts = 3;
    const delay = attempt === 0 ? 100 : 1000 * attempt;

    setTimeout(() => {
      if (
        this.userService.jwtToken &&
        this.organizationService.currentOrganization?.alias &&
        this.userService.userId &&
        (!this.websocketService.status.value ||
          this.websocketService.status.value === 'DISCONNECTED')
      ) {
        this.websocketService.initialize();

        if (attempt < maxAttempts - 1) {
          setTimeout(() => {
            if (this.websocketService.status.value === 'DISCONNECTED') {
              this.initializeWebsocketWithRetry(attempt + 1);
            }
          }, 2000);
        }
      }
    }, delay);
  }

  public getSidebarStatus(): boolean {
    return SessionConfigService.getSidebarStatus();
  }

  public isLogin(): boolean {
    const urlParts = window.location.href.split('/');
    return urlParts[urlParts.length - 1] === 'login';
  }

  public logout(): void {
    this.userService.logout();
    this.router.navigate(['/login']).catch(error => {
      throw error;
    });
  }

  public openHelpDialog(): void {
    this.modal.open(HelpDialogComponent, {});
  }

  private listenToPermissions(): void {
    this.organizationService.permissionsLoaded
      .pipe(delay(0))
      .subscribe((isLoaded: boolean) => {
        this.permissionsLoaded = isLoaded;
      });
  }

  public dismissAwsOutbreakBanner(): void {
    this.awsOutbreakBannerActive = false;
  }
}
